import React, { useEffect, useRef, useState } from "react";
import { PRODUCT_ORDER } from "@merge-api/merge-javascript-shared";
import type { ProductKey } from "@merge-api/merge-javascript-shared";

interface Params {
  products: ProductKey[];
  options: ProductKey[];
  lockedProduct: ProductKey | null;
  onFinish: (nextProducts: ProductKey[]) => void;
}

const inProductOrder = (products: ProductKey[]) =>
  PRODUCT_ORDER.filter((product) => products.includes(product));

const useProductsDraft = ({ products, options, lockedProduct, onFinish }: Params) => {
  // state
  const [draft, setDraft] = useState<ProductKey[] | null>(null);

  // hooks
  const typeaheadRef = useRef<HTMLDivElement>(null);

  // derived state
  const isEditing = draft !== null;
  const shownProducts = inProductOrder(products.filter((product) => options.includes(product)));

  // effects
  // The Typeahead has no autoFocus prop, and focusing its input is what opens the listbox.
  useEffect(() => {
    if (isEditing) {
      typeaheadRef.current?.querySelector("input")?.focus();
    }
  }, [isEditing]);

  // event handlers
  const startEditing = () => setDraft(shownProducts);

  const isOptionDisabled = (product: ProductKey) =>
    product === lockedProduct || (draft?.length === 1 && draft[0] === product);

  const finishEditing = () => {
    if (draft === null) {
      return;
    }
    setDraft(null);

    const nextProducts = inProductOrder([
      ...draft,
      ...products.filter((product) => !options.includes(product)),
    ]);
    const isUnchanged =
      nextProducts.length === products.length &&
      nextProducts.every((product) => products.includes(product));
    if (!isUnchanged) {
      onFinish(nextProducts);
    }
  };

  const onDraftChange = (value: ProductKey[] | null) => {
    setDraft((previous) => {
      if (!previous) return previous;
      if (!value?.length) return [...previous];
      const kept =
        lockedProduct && !value.includes(lockedProduct) ? [...value, lockedProduct] : value;
      return inProductOrder(kept);
    });
  };

  const onTypeaheadBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const nextFocus = event.relatedTarget as HTMLElement | null;
    // The Select all row's headlessui Switch.Label focuses its switch on click, so focus is handed back to the input.
    if (nextFocus?.closest("[role='listbox']")) {
      setTimeout(() => typeaheadRef.current?.querySelector("input")?.focus());
      return;
    }
    if (!event.currentTarget.contains(nextFocus)) {
      finishEditing();
    }
  };

  const onTypeaheadKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      (event.target as HTMLElement).blur();
    }
  };

  return {
    draft,
    shownProducts,
    typeaheadRef,
    startEditing,
    isOptionDisabled,
    onDraftChange,
    onTypeaheadBlur,
    onTypeaheadKeyDown,
  };
};

export default useProductsDraft;
